import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Panel, btn } from "@/components/ui-kit";
import { OrderEditor } from "@/components/sap/OrderEditor";
import { SapResults } from "@/components/sap/SapResults";

const Badge = ({ o }) => {
  const [t, c] = o.duplicate ? ["Duplicate", "bg-amber-500/15 text-amber-300"] : o.errors?.length ? ["Errors", "bg-rose-500/15 text-rose-300"] : ["Ready", "bg-emerald-500/15 text-emerald-300"];
  return <span className={`rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase ${c}`}>{t}</span>;
};

export const SapPreview = ({ batch, onDone, source = "" }) => {
  const [orders, setOrders] = useState(batch.orders);
  const [open, setOpen] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const ready = orders.filter((o) => !o.duplicate && !o.errors?.length).length;
  const create = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/sap/create", { batch_id: batch.id, source, orders })).data.results;
      setResults(r);
      toast.success(`${r.filter((x) => !["duplicate", "error"].includes(x.status)).length} order(s) created`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  if (results) return <SapResults results={results} />;
  return (
    <Panel title={`Preview — ${batch.filename} · ${orders.length} order(s), ${batch.rows} row(s)`} testId="sap-preview"
      action={<Button onClick={create} disabled={busy || !orders.length} className={btn.primary} data-testid="sap-create-btn">{busy ? "Creating…" : `Create orders (${ready} ready)`}</Button>}>
      <div className="divide-y divide-slate-800">
        {orders.map((o, i) => (
          <div key={i} data-testid={`sap-order-${i}`}>
            <button onClick={() => setOpen(open === i ? null : i)} className="flex w-full flex-wrap items-center gap-3 px-5 py-3 text-left text-sm hover:bg-white/[0.02]" data-testid={`sap-order-toggle-${i}`}>
              {open === i ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
              <Badge o={o} />
              <span className="font-mono text-slate-200">SO {o.sales_order || "—"}</span>
              <span className="font-mono text-sky-300">PO {o.po_number}</span>
              <span className="text-slate-400">{o.ship_to?.code} · {o.lines.length} line(s) · {o.site_code || "auto site"}</span>
              {o.errors?.length > 0 && <span className="w-full pl-7 text-xs text-rose-300" data-testid={`sap-order-errors-${i}`}>{o.errors.join(" · ")}</span>}
              {!o.errors?.length && o.warnings?.length > 0 && <span className="w-full pl-7 text-xs text-slate-500">{o.warnings.join(" · ")}</span>}
            </button>
            {open === i && <div className="px-5 pb-5"><OrderEditor o={o} prefix={`sap-edit-${i}`} set={(v) => setOrders(orders.map((x, j) => (j === i ? { ...v, errors: [], duplicate: false } : x)))} /></div>}
          </div>
        ))}
      </div>
      <p className="px-5 py-3 text-xs text-slate-500">Fix any line by opening the order. Corrections are re-checked when you click Create; duplicates and orders with errors are skipped and listed.</p>
    </Panel>
  );
};
